import { NewPassword } from '../../components/NewPassword';
import { SignInLink } from '../../components/SignInLink';
import { alreadyExists, errorText } from '../../errorText';
import { readApiError } from '../../apiError';
import { passwordRule } from '../../password';

/** iam's "A code was just sent… wait N seconds": RESOURCE_EXHAUSTED on account init. */
const codeJustSent = (e: unknown) => {
    const { code, status, raw } = readApiError(e);
    return (code === 8 || status === 429) && /code was just sent/i.test(raw);
};
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createAccountSchema } from './schema';
import { Flex, Button, Text, TextField, Callout, Separator, Link } from '@dodilio/ui-design-lib';
import { CreateAccountCore } from '../../types/register';
import { z } from 'zod';
import { useSetAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { createAccountAtom } from '../../state';
import { ServiceRegister } from '../../api';
import { readClientInvite } from '../../clientInvite';
import { endSignupSession, signUpWithGoogle } from '../../auth';
import { useBackForward } from '../../useBackForward';
import { signupLinkAtStart } from '../../signupLink';
import { rememberSignupEmail } from '../../signupEmail';

type FormData = z.infer<typeof createAccountSchema>;


export const CreateAccountForm = () => {
    const nav = useNavigate();
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState<string>('');
    const [exists, setExists] = useState(false);
    const setAccountInfo = useSetAtom(createAccountAtom);
    const saved = useAtomValue(createAccountAtom);
    // A partner's client invitation is for one address: the account must use it.
    const invitedEmail = useMemo(() => readClientInvite()?.email, []);
    // From Keycloak's sign-in page: the email they typed (still editable), and Google if they chose it.
    const [link] = useState(signupLinkAtStart);
    const defaults = useMemo<FormData>(() => ({
        email: invitedEmail ?? saved?.email ?? link.email ?? '',
        password: '',
    }), [invitedEmail, saved?.email, link.email]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting, dirtyFields, isSubmitted },
    } = useForm<FormData>({
        resolver: zodResolver(createAccountSchema),
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: defaults,
    });

    const emailValue = watch('email');
    // The email input is autoFocused, and validation runs on blur — so clicking
    // "Sign up with Google" blurred an EMPTY, never-typed field and greeted the
    // user with "that doesn't look like a valid email" for choosing the other
    // path. Only surface the error once they've actually typed something, or
    // once they've pressed Proceed.
    const showEmailError = !!errors.email && (!!dirtyFields.email || isSubmitted);
    // The password fields appear as soon as the email looks whole, while typing, so Tab
    // from the email reaches them (waiting for blur made Tab skip past them).
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue ?? '');
    // `window.location.href` gives no feedback until the browser actually
    // navigates — on a slow connection the button looked dead and invited a
    // second click. Track it so the button can say it's working.
    const [googleRedirecting, setGoogleRedirecting] = useState(false);
    // Back from Google: the button works again.
    useBackForward(useCallback(() => setGoogleRedirecting(false), []));
    const startGoogle = useCallback(() => {
        setGoogleRedirecting(true);
        signUpWithGoogle(`${window.location.origin}/signup/org`).catch(() => {
            setGoogleRedirecting(false);
            setStatus('error');
            setMessage('Dodil couldn\'t be reached. Check your connection and try again.');
        });
    }, []);
    // They chose Google on the sign-in page already: one click, not two. Once per page.
    const googleStarted = useRef(false);
    useEffect(() => {
        if (!link.google || googleStarted.current) return;
        googleStarted.current = true;
        startGoogle();
    }, [link.google, startGoogle]);

    const onSubmit = async (data: FormData) => {
        // A new signup: nothing from an earlier one in this tab (its session mark) carries over.
        endSignupSession();
        const toCode = () => {
            setStatus('idle');
            setMessage('');
            const x: CreateAccountCore = { email: data.email, password: data.password };
            setAccountInfo(x);
            rememberSignupEmail(data.email);
            nav('/verify');
        };
        try {
            await ServiceRegister.createAccountInit({ email: data.email });
            toCode();
        } catch (err) {
            // A code was just sent to this address (the tab was closed, say): it still works, so go and enter it.
            if (codeJustSent(err)) { toCode(); return; }
            setStatus('error');
            setExists(alreadyExists(err));
            setMessage(errorText(err, 'account'));
        }
    };

    useEffect(() => {
        if (saved) {
            reset({
                email: invitedEmail ?? saved.email ?? '',
                password: '',
                    });
        }
    }, [saved, reset]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {status !== 'idle' && (
                <Callout.Root
                    mb="4"
                    size="1"
                    className="api-banner"
                    variant="soft"
                    color={status === 'success' ? 'green' : 'tomato'}
                    role={status === 'error' ? 'alert' : undefined}
                >
                    <Callout.Text>
                        {message}
                        {exists && <>{' '}<SignInLink email={(emailValue ?? '').trim() || undefined} /></>}
                    </Callout.Text>
                </Callout.Root>
            )}
            {status !== 'success' && (
                <Flex direction="column" gap="3">
                    {/* Email */}
                    <TextField.Root
                        {...register('email')}
                        size="2"
                        color={showEmailError ? 'tomato' : 'gray'}
                        type="email"
                        variant="surface"
                        autoFocus={!invitedEmail}
                        readOnly={!!invitedEmail}
                        placeholder="johndoe@acme-inc.com"
                        aria-label="Email"
                        aria-describedby={invitedEmail ? 'invited-email-note' : undefined}
                    >
                        <TextField.Slot className="fieldLabel">
                            <Text size="1">Email</Text>
                        </TextField.Slot>
                    </TextField.Root>
                    {showEmailError && <Text size="1" className="field-error" role="alert">{errors.email?.message}</Text>}
                    {invitedEmail && (
                        <Text size="1" color="gray" id="invited-email-note">Your invitation was sent to this address, so your account uses it.</Text>
                    )}

                    {isEmailValid && (<>
                        <NewPassword field={register('password')} value={watch('password') ?? ''} error={errors.password?.message} />
                    </>)}

                    {/* Primary action — solid so it outranks the Google button
                        below it. Both were variant="surface", which made the
                        one that submits the form look identical to the
                        alternative sign-in beneath it. */}
                    <Button
                        radius="full"
                        type="submit"
                        size="3"
                        variant="solid"
                        mt="4"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        Proceed
                    </Button>
                    {/* The code from an earlier try still works: no need to wait for another. */}
                    {isEmailValid && (
                        <Text size="2" color="gray" align="center">
                            <Link
                                href="/verify"
                                underline="always"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    const email = (emailValue ?? '').trim();
                                    // A password that doesn't meet the rules yet isn't carried: the code step asks for one.
                                    const typed = watch('password') ?? '';
                                    setAccountInfo({ email, password: passwordRule.safeParse(typed).success ? typed : '' });
                                    rememberSignupEmail(email);
                                    nav('/verify');
                                }}
                            >
                                I already have a code
                            </Link>
                        </Text>
                    )}

                    <Flex align="center" gap="3" my="2">
                        <Separator size="4" style={{ flex: 1 }} />
                        <Text size="1" color="gray">or</Text>
                        <Separator size="4" style={{ flex: 1 }} />
                    </Flex>

                    {/* Google Sign Up */}
                    <Button
                        radius="full"
                        type="button"
                        size="3"
                        variant="surface"
                        disabled={googleRedirecting || isSubmitting}
                        loading={googleRedirecting}
                        onClick={startGoogle}
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 8 }}>
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.003 24.003 0 000 21.56l7.98-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                        {googleRedirecting ? 'Redirecting to Google…' : 'Sign up with Google'}
                    </Button>

                    {/* Through the org step: one without an organization makes it there. */}
                    <Text size="2" color="gray" align="center" mt="2">
                        Already have an account?{' '}
                        <SignInLink email={isEmailValid ? (emailValue ?? '').trim() : undefined} />
                    </Text>
                </Flex>
            )}
        </form>
    );
};
